import { writable, derived, get } from 'svelte/store';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User as FirebaseUser,
  type Auth,
} from 'firebase/auth';
import { api } from '$lib/api/client';
import type { User } from '$lib/api/types';

// Firebase config from environment
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

// Check if using emulator
const useEmulator = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR === 'true';

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;

// Auth state
interface AuthState {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  initialized: boolean;
}

const initialState: AuthState = {
  firebaseUser: null,
  user: null,
  loading: true,
  initialized: false,
};

export const authStore = writable<AuthState>(initialState);

// Derived stores
export const isAuthenticated = derived(authStore, ($auth) => !!$auth.user);
export const currentUser = derived(authStore, ($auth) => $auth.user);
export const isLoading = derived(authStore, ($auth) => $auth.loading);
export const isInitialized = derived(authStore, ($auth) => $auth.initialized);
// Firebase auth complete but no DB user (needs registration)
export const needsRegistration = derived(
  authStore,
  ($auth) => $auth.initialized && !$auth.loading && !!$auth.firebaseUser && !$auth.user
);

// Get ID token for API requests
export async function getIdToken(): Promise<string | null> {
  const { firebaseUser } = get(authStore);
  if (!firebaseUser) {
    // Development mode fallback
    if (import.meta.env.DEV && !firebaseConfig.apiKey) {
      return 'dev-token-dev-user-123';
    }
    return null;
  }
  return firebaseUser.getIdToken();
}

// Initialize Firebase and auth listener
export function initAuth() {
  // Skip Firebase init if not configured (development mode)
  if (!firebaseConfig.apiKey && !useEmulator) {
    console.warn('Firebase not configured. Running in development mode.');
    authStore.update((state) => ({
      ...state,
      loading: false,
      initialized: true,
    }));
    return;
  }

  if (!app) {
    // For emulator mode, use a minimal config
    const config = useEmulator
      ? { apiKey: 'fake-api-key', projectId: 'demo-project' }
      : firebaseConfig;
    app = initializeApp(config);
  }

  if (!authInstance) {
    authInstance = getAuth(app);

    // Connect to emulator if enabled
    if (useEmulator) {
      connectAuthEmulator(authInstance, 'http://localhost:9099', { disableWarnings: true });
      console.log('Connected to Firebase Auth Emulator');
    }
  }

  onAuthStateChanged(authInstance, async (firebaseUser) => {
    if (firebaseUser) {
      authStore.update((state) => ({
        ...state,
        firebaseUser,
        loading: true,
      }));

      // Try to fetch user data
      try {
        const user = await api.users.getMe();
        authStore.update((state) => ({
          ...state,
          user,
          loading: false,
          initialized: true,
        }));
      } catch (error) {
        // User not registered yet
        authStore.update((state) => ({
          ...state,
          user: null,
          loading: false,
          initialized: true,
        }));
      }
    } else {
      authStore.set({
        firebaseUser: null,
        user: null,
        loading: false,
        initialized: true,
      });
    }
  });
}

// Sign in with Google (or email/password for emulator)
export async function signIn(): Promise<void> {
  if (!firebaseConfig.apiKey && !useEmulator) {
    // Development mode: mock sign in
    console.warn('Development mode: mock sign in');
    authStore.update((state) => ({
      ...state,
      firebaseUser: { uid: 'dev-user-123' } as FirebaseUser,
      loading: true,
    }));

    try {
      const user = await api.users.getMe();
      authStore.update((state) => ({
        ...state,
        user,
        loading: false,
      }));
    } catch {
      authStore.update((state) => ({
        ...state,
        user: null,
        loading: false,
      }));
    }
    return;
  }

  if (!authInstance) {
    throw new Error('Auth not initialized');
  }

  const provider = new GoogleAuthProvider();

  try {
    await signInWithPopup(authInstance, provider);
  } catch (error) {
    console.error('Sign in error:', error);
    throw error;
  }
}

// Sign in with email/password (for emulator testing)
export async function signInWithEmail(email: string, password: string): Promise<void> {
  if (!authInstance) {
    throw new Error('Auth not initialized');
  }

  try {
    await signInWithEmailAndPassword(authInstance, email, password);
  } catch (error: any) {
    // If user doesn't exist, create them (for emulator)
    if (error.code === 'auth/user-not-found' && useEmulator) {
      await createUserWithEmailAndPassword(authInstance, email, password);
    } else {
      throw error;
    }
  }
}

// Create account with email/password (for emulator testing)
export async function createAccountWithEmail(email: string, password: string): Promise<void> {
  if (!authInstance) {
    throw new Error('Auth not initialized');
  }

  await createUserWithEmailAndPassword(authInstance, email, password);
}

// Sign out
export async function signOut(): Promise<void> {
  if (!firebaseConfig.apiKey && !useEmulator) {
    // Development mode: mock sign out
    authStore.set({
      firebaseUser: null,
      user: null,
      loading: false,
      initialized: true,
    });
    return;
  }

  if (!authInstance) {
    return;
  }

  await firebaseSignOut(authInstance);
}

// Register new user
export async function register(username: string): Promise<User> {
  const user = await api.users.create({ username });
  authStore.update((state) => ({
    ...state,
    user,
  }));
  return user;
}

// Update user in store
export function updateUserInStore(user: User): void {
  authStore.update((state) => ({
    ...state,
    user,
  }));
}
